/**
 * Catálogo de roles de Gestión Usuarios.
 *
 * Los nombres son la ÚNICA fuente de verdad para decidir autorización.
 * Los ids numéricos de Gestión Usuarios NO son estables entre entornos:
 * un mismo rol tiene distinto id en desarrollo y en producción, por lo que
 * comparar por id produce decisiones correctas en un entorno e incorrectas
 * en otro.
 *
 * Ningún componente debe escribir un nombre de rol como literal: importar
 * siempre desde aquí.
 */
export const GU_ROLES = {
	ADMINISTRADOR: 'ADMINISTRADOR',

	/** Sin la preposición DE, a diferencia de RESPONSABLE_DE_CONTRATISTA. */
	COORDINADOR_CONTRATISTA: 'COORDINADOR CONTRATISTA',

	RESPONSABLE_DE_CONTRATISTA: 'RESPONSABLE DE CONTRATISTA',
	GESTOR_SSOMA_EXTERNO: 'GESTOR SSOMA EXTERNO',
	SUPERVISOR_DE_ZONA: 'SUPERVISOR DE ZONA',
	TECNICO: 'TECNICO',

	/** Con guion bajo; el resto del catálogo usa espacios. */
	TECNICO_INT: 'TECNICO_INT',
} as const;

// El catálogo de Gestión Usuarios contiene más roles (GESTOR DESPLIEGE,
// GESTOR OYM, VISUALIZADOR, entre otros). Aquí se declaran únicamente los que
// intervienen en alguna decisión del código. Al incorporar uno nuevo,
// verificar su nombre exacto contra GET /api/role/roles/.

export type GuRoleName = typeof GU_ROLES[keyof typeof GU_ROLES];

/**
 * Normaliza un nombre de rol para compararlo.
 *
 * Distintos endpoints devuelven el mismo rol con capitalización diferente
 * (ADMINISTRADOR y Administrador conviven en el ecosistema), por lo que la
 * comparación nunca debe hacerse con igualdad directa.
 */
export const normalizeRoleName = (roleName: string | null | undefined): string =>
	(roleName ?? '').trim().toUpperCase();

/**
 * Filtra un listado de roles devuelto por el backend, dejando únicamente los
 * que figuran en `allowed`.
 *
 * Compara por nombre normalizado, nunca por id. Un rol sin nombre se descarta.
 *
 * @param roles   listado tal como lo devuelve Gestión Usuarios
 * @param allowed nombres admitidos, importados de `GU_ROLES`
 */
export const filterRolesByName = <T extends { name?: string }>(
	roles: T[],
	allowed: string[]
): T[] => {
	const wanted = (allowed ?? []).map(normalizeRoleName).filter(Boolean);
	if (!wanted.length) return [];

	return (roles ?? []).filter((role) => {
		const name = normalizeRoleName(role?.name);
		return !!name && wanted.includes(name);
	});
};
